'use client';

import clsx from 'clsx';
import Image from 'next/image';

import failure1 from '@/sections/product/failure-analytics/FailureAnalytics_Identify.jpg';
import failure2 from '@/sections/product/failure-analytics//FailureAnalytics_Investigate_Source.jpg';
import failure3 from '@/sections/product/failure-analytics//FailureAnalytics_ViewBuildFailureHistory.jpg';
import failure4 from '@/sections/product/failure-analytics//FailureAnalytics_TriageFailedTests.jpg';
import failure5 from '@/sections/product/failure-analytics//FailureAnalytics_StreamlineRCA.jpg';

const benefitsData = [
  {
    id: 1,
    title: 'Identify the meaningful failures',
    body: "With Develocity's automatic categorization of verification vs. non-verification failures, you can focus on fixing the code causing “real” failures and better collaborate on eliminating the pesky non-verification failures.",
    content: (
      <div className="relative aspect-[1.4/1] h-auto w-full lg:aspect-[1.2/1]">
        <Image
          src={failure1}
          alt="data points shown on a dark themed graph"
          fill
          className="rounded-2xl object-cover"
        />
      </div>
    ),
    gridClass: 'col-span-2 lg:col-span-1',
  },
  {
    id: 2,
    title: 'Investigate failed builds',
    body: 'Detect, group, and prioritize avoidable build failures for remediation based on impact. You can also more quickly and easily determine the root cause of the incident via dashboards for build and CI failure metrics.',
    content: (
      <div className="relative aspect-[1.4/1] h-auto w-full self-end lg:aspect-[1.2/1]">
        <Image
          src={failure2}
          alt="data points shown on a dark themed graph"
          fill
          className="rounded-2xl object-cover"
        />
      </div>
    ),
    gridClass: 'col-span-2 lg:col-span-1',
  },
  {
    id: 3,
    title: 'View build failure history',
    body: "See the failure history across all local and CI builds, common traits among failed runs, and differences when compared to successful runs. You can then measure the impact of failures and see whether it's decreasing over time.",
    content: (
      <div className="relative aspect-[2.2/1] h-auto w-full">
        <Image
          src={failure3}
          alt="data points shown on a dark themed graph"
          fill
          className="rounded-2xl object-cover"
        />
      </div>
    ),
    gridClass: 'col-span-2',
  },
  {
    id: 4,
    title: 'Triage failed tests',
    body: "Separate the tests telling you something valuable when they fail—for example, that there's a problem in the production code—from flaky tests, so you can address the root cause of each.",
    content: (
      <div className="relative aspect-[1.4/1] h-auto w-full lg:aspect-[1.2/1]">
        <Image
          src={failure4}
          alt="data points shown on a dark themed graph"
          fill
          className="rounded-2xl object-cover"
        />
      </div>
    ),
    gridClass: 'col-span-2 lg:col-span-1',
  },
  {
    id: 5,
    title: 'Streamline root cause analysis',
    body: 'View a listing of build environment metadata plus performance characteristics for failed test executions compared to passing ones. See the “before” of when particular tests started to fail to determine root cause.',
    content: (
      <div className="relative aspect-[1.4/1] h-auto w-full lg:aspect-[1.2/1]">
        <Image
          src={failure5}
          alt="data points shown on a dark themed graph"
          fill
          className="rounded-2xl object-cover"
        />
      </div>
    ),
    gridClass: 'col-span-2 lg:col-span-1',
  },
];

export default function Benefits() {
  return (
    <section
      id="benefits"
      className="min-h-screen-with-nav py-section text-body grid bg-gray-1 px-4 text-center text-white md:px-10"
    >
      <div className="max-w-section grid h-full grid-cols-2 gap-6 md:px-12">
        {benefitsData.map(item => (
          <div
            key={item.id.toString()}
            className={clsx(
              'flex flex-col justify-between rounded-3xl border border-dark-background bg-[#13171B] p-8 text-left md:p-10',
              item.gridClass,
            )}
          >
            <div className="mb-8 flex flex-col gap-4">
              <h4 className="text-h6">{item.title}</h4>
              <p className="text-body max-w-3xl text-gray-4">{item.body}</p>
            </div>
            {item.content}
          </div>
        ))}
      </div>
    </section>
  );
}
